import { ButtonVariants } from '@components/button/Button';
import { LinkStyledAsButton } from '@components/linkStyledAsButton/LinkStyledAsButton';
import { sentryCreateBrowserRouter } from '@utils/sentry';
import {
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
} from 'react-router-dom';
import { CreateAccountPage } from './auth/CreateAccountPage';
import { ForgotPasswordPage } from './auth/ForgotPasswordPage';
import { InviteExpiredPage } from './auth/InviteExpiredPage';
import { IsAuthenticated } from './auth/IsAuthenticated';
import { IsManager } from './auth/IsManager';
import { LoginPage } from './auth/LoginPage';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { PublishedListingsContextProvider } from './context/PublishedListingsContext';
import { ServicePageModalContextProvider } from './context/ServicePageModalContext';
import { ErrorNoRestaurants } from './errors/ErrorNoRestaurants';
import { ErrorHandler } from './errors/ErrorHandler';
import { NotFoundHero } from './errors/NotFoundHero';
import { GuestBookPage } from './guestBook/GuestBookPage';
import { Layout } from './layout/Layout';
import { PageContent } from './layout/PageContent';
import { InboxPage } from './messaging/InboxPage';
import { RestaurantBulletinForm } from './operations/bulletins/RestaurantBulletinForm';
import { RestaurantBulletinsPage } from './operations/bulletins/RestaurantBulletinsPage';
import { FloorPlansPage } from './operations/floorPlans/FloorPlansPage';
import { CreateListingPage } from './operations/listings/create/CreateListingPage';
import { EditListingPage } from './operations/listings/edit/EditListingPage';
import { ListingsContextProvider } from './operations/listings/ListingsContext';
import { ListingsFloorPlanPage } from './operations/listings/listingsPage/ListingsFloorPlanPage';
import { MessagingPage } from './operations/messaging/MessagingPage';
import { MessagingTemplatePage } from './operations/messaging/MessagingTemplatePage';
import { TableRatingPage } from './operations/pricing/TableRatingPage';
import { BusyTimesPage } from './operations/pricing/BusyTimesPage';
import { ForecastPage } from './operations/pricing/ForecastPage';
import { RestaurantEventsPage } from './operations/restaurantEvents/RestaurantEventsPage';
import {
  COVER_FLOW_PATH,
  CREATE_ACCOUNT_PATH,
  FORGOT_PASSWORD_ROOT_PATH,
  GUEST_BOOK_GUEST_PATH,
  INBOX_PATH,
  INVITE_EXPIRED_PATH,
  OPERATIONS_BULLETIN_BOARD_CREATE_TERMINAL_PATH,
  OPERATIONS_BULLETIN_BOARD_TERMINAL_PATH,
  OPERATIONS_BULLETIN_BOARD_UPDATE_TERMINAL_PATH,
  OPERATIONS_EVENTS_PATH,
  OPERATIONS_EVENT_BOOK_TERMINAL_PATH,
  OPERATIONS_EVENT_DETAIL_NEW_TERMINAL_PATH,
  OPERATIONS_EVENT_DETAIL_TERMINAL_PATH,
  OPERATIONS_EVENTS_TERMINAL_PATH,
  OPERATIONS_FLOOR_PLANS_TERMINAL_PATH,
  OPERATIONS_FORECAST_TERMINAL_PATH,
  OPERATIONS_LISTINGS_CALENDAR_TERMINAL_PATH,
  OPERATIONS_LISTINGS_CREATE_TERMINAL_PATH,
  OPERATIONS_LISTINGS_EDIT_TERMINAL_PATH,
  OPERATIONS_LISTINGS_FLOOR_PLAN_INACTIVE_TERMINAL_PATH,
  OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH,
  OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_TERMINAL_PATH,
  OPERATIONS_LISTINGS_FLOOR_PLAN_TERMINAL_PATH,
  OPERATIONS_LISTINGS_TERMINAL_PATH,
  OPERATIONS_INTEGRATIONS_HOTEL_SYNC_PATH,
  OPERATIONS_INTEGRATIONS_HOTEL_SYNC_TERMINAL_PATH,
  OPERATIONS_INTEGRATIONS_TERMINAL_PATH,
  OPERATIONS_MESSAGING_TEMPLATE_PATH,
  OPERATIONS_MESSAGING_TERMINAL_PATH,
  OPERATIONS_PRICING_GRID_TERMINAL_PATH,
  OPERATIONS_RESERVATION_TIMELINE_TERMINAL_PATH,
  OPERATIONS_BUSY_TIMES_TERMINAL_PATH,
  OPERATIONS_ROOT_PATH,
  OPERATIONS_SHIFTS_TERMINAL_PATH,
  OPERATIONS_TABLE_RATING_TERMINAL_PATH,
  RESERVATIONS_CONCIERGE_MULTIPLE_TABLES_TERMINAL_PATH,
  RESERVATIONS_CONCIERGE_TERMINAL_PATH,
  RESERVATIONS_COVERS_REPORT_PATH,
  RESERVATIONS_OCCUPANTS_PAGE_PATH,
  RESERVATIONS_OCCUPANTS_TERMINAL_PATH,
  RESERVATIONS_RESERVATIONS_REPORT_PATH,
  RESERVATIONS_ROOT_PATH,
  RESERVATIONS_SERVERS_PAGE_PATH,
  RESERVATIONS_SERVICE_PATH,
  RESERVATIONS_SERVICE_TERMINAL_PATH,
  RESERVATIONS_WAITLIST_PAGE_PATH,
  RESET_PASSWORD_ROOT_PATH,
  SETTINGS_EDIT_TEAM_MEMBER_TERMINAL_PATH,
  SETTINGS_FINANCES_TERMINAL_PATH,
  SETTINGS_GENERAL_PATH,
  SETTINGS_GENERAL_TERMINAL_PATH,
  SETTINGS_INVITE_MEMBER_TERMINAL_PATH,
  SETTINGS_LIGHTSPEED_CALLBACK_TERMINAL_PATH,
  SETTINGS_LIGHTSPEED_TERMINAL_PATH,
  SETTINGS_REPORTS_TERMINAL_PATH,
  SETTINGS_ROOT_PATH,
  SETTINGS_TEAM_TERMINAL_PATH,
  SETTINGS_VIP_PROGRAM_TERMINAL_PATH,
  SUPPORT_GUEST_IMPORT_TERMINAL_PATH,
  SUPPORT_ROOT_PATH,
} from './paths';
import { Root } from './Root';
import { ReportsPage } from './reports/ReportsPage';
import { ConciergeAvailabilityContextProvider } from './reservations/concierge/ConciergeAvailabilityContext';
import { ConciergePage } from './reservations/concierge/ConciergePage';
import { AvailabilityDrawerContextProvider } from './reservations/concierge/state/AvailabilityDrawerContext';
import { DailyCoversReportPage } from './reservations/report/covers/DailyCoversReportPage';
import { DailyReservationsReportPage } from './reservations/report/reservations/DailyReservationsReportPage';
import { OccupantsSidePanel } from './reservations/service/OccupantsSidePanel';
import { ServersSidePanel } from './reservations/service/ServersSidePanel';
import { ServiceMultiSelectionProvider } from './reservations/service/ServiceMultiSelectionContext';
import { ServicePage } from './reservations/service/ServicePage';
import { ServerFormContextProvider } from './reservations/service/sidePanel/servers/ServerFormContext';
import { WaitListContextProvider } from './reservations/service/sidePanel/waitList/state/WaitListContext';
import { ReservationServiceContextProvider } from './reservations/service/state/ReservationServiceContext';
import { ReservationServiceSelectedSeatingContextProvider } from './reservations/service/state/ReservationServiceSelectedSeatingContext';
import { ServiceDrawer } from './reservations/service/ServiceDrawer';
import { WaitlistSidePanel } from './reservations/service/WaitlistSidePanel';
import { SplitMergeTablesProvider } from './reservations/splitMergeTables/state/SplitMergeTablesContext';
import { ReservationTimelinePage } from './reservationTimeline/ReservationTimelinePage';
import { FinancesPage } from './settings/finances/FinancesPage';
import { GeneralPage } from './settings/general/GeneralPage';
import { SettingsPage } from './settings/SettingsPage';
import { EditTeamMemberPage } from './settings/team/EditTeamMemberPage';
import { InviteTeamMemberPage } from './settings/team/InviteTeamMemberPage';
import { TeamPage } from './settings/team/TeamPage';
import { VipProgramPage } from './settings/vipProgram/VipProgramPage';
import { GuestImport } from './support/GuestImport';
import { LightspeedSettingsPage } from './settings/lightspeed/LightspeedSettingsPage';
import { LightspeedOAuthCallbackPage } from './settings/lightspeed/LightspeedOAuthCallbackPage';

const servicePageWithContexts = (
  <ServicePageModalContextProvider>
    <ReservationServiceContextProvider>
      <SplitMergeTablesProvider>
        <WaitListContextProvider>
          <PublishedListingsContextProvider>
            <ServerFormContextProvider>
              <ServiceMultiSelectionProvider>
                <ReservationServiceSelectedSeatingContextProvider>
                  <ServicePage />
                </ReservationServiceSelectedSeatingContextProvider>
              </ServiceMultiSelectionProvider>
            </ServerFormContextProvider>
          </PublishedListingsContextProvider>
        </WaitListContextProvider>
      </SplitMergeTablesProvider>
    </ReservationServiceContextProvider>
  </ServicePageModalContextProvider>
);

const reservationServiceDrawerWrapper = (
  <ReservationServiceContextProvider>
    <Outlet />
    <ServiceDrawer allowActivationOfSeatMode={false} />
  </ReservationServiceContextProvider>
);

// The ag-grid-* family of dependencies is massive, and consumed by several non-entrypoint pages.
// We consolidate the import of these pages into the dedicated module below so that we can code-split them.
// This ensures that the ag-grid-* dependencies aren't 'hoisted' into the entrypoint chunk
const pagesThatConsumeAgGridImportTrigger = async () => {
  return await import(/* webpackPrefetch: true */ './pagesThatConsumeAgGrid');
};

export const router = sentryCreateBrowserRouter(
  createRoutesFromElements(
    <Route element={<Root />} errorElement={<ErrorHandler />}>
      <Route element={<LoginPage />} index />
      <Route
        element={<ForgotPasswordPage />}
        path={FORGOT_PASSWORD_ROOT_PATH}
      />
      <Route element={<ResetPasswordPage />} path={RESET_PASSWORD_ROOT_PATH} />
      <Route element={<CreateAccountPage />} path={CREATE_ACCOUNT_PATH} />
      <Route element={<InviteExpiredPage />} path={INVITE_EXPIRED_PATH} />
      <Route element={<IsAuthenticated />}>
        <Route element={<Layout />} errorElement={<ErrorNoRestaurants />}>
          <Route errorElement={<ErrorHandler />}>
            <Route path={`${RESERVATIONS_ROOT_PATH}/*`}>
              <Route
                element={servicePageWithContexts}
                path={RESERVATIONS_SERVICE_TERMINAL_PATH}
              >
                <Route index element={<Navigate to="occupants" replace />} />
                <Route
                  path={RESERVATIONS_OCCUPANTS_PAGE_PATH}
                  element={<OccupantsSidePanel />}
                />
                <Route
                  path={RESERVATIONS_WAITLIST_PAGE_PATH}
                  element={<WaitlistSidePanel />}
                />
                <Route
                  path={RESERVATIONS_SERVERS_PAGE_PATH}
                  element={<ServersSidePanel />}
                />
              </Route>

              <Route element={reservationServiceDrawerWrapper}>
                <Route
                  element={
                    <ConciergeAvailabilityContextProvider>
                      <AvailabilityDrawerContextProvider>
                        <ConciergePage />
                      </AvailabilityDrawerContextProvider>
                    </ConciergeAvailabilityContextProvider>
                  }
                  path={RESERVATIONS_CONCIERGE_TERMINAL_PATH}
                />
                <Route
                  element={
                    <ConciergeAvailabilityContextProvider multiTableMode>
                      <AvailabilityDrawerContextProvider>
                        <ConciergePage />
                      </AvailabilityDrawerContextProvider>
                    </ConciergeAvailabilityContextProvider>
                  }
                  path={RESERVATIONS_CONCIERGE_MULTIPLE_TABLES_TERMINAL_PATH}
                />
                {/* TODO: deprecated - remove this route and path when the iPad app no longer links to it */}
                <Route
                  element={
                    <PageContent>
                      <LinkStyledAsButton
                        variant={ButtonVariants.Primary}
                        to={RESERVATIONS_SERVICE_PATH}
                        label="Occupants Page Functionality Has Moved - Click Here"
                      />
                    </PageContent>
                  }
                  path={RESERVATIONS_OCCUPANTS_TERMINAL_PATH}
                />
              </Route>
            </Route>
            <Route element={reservationServiceDrawerWrapper}>
              <Route element={<GuestBookPage />} path={GUEST_BOOK_GUEST_PATH} />
              <Route
                path={COVER_FLOW_PATH}
                lazy={async () => {
                  const { CoverFlowPage } =
                    await pagesThatConsumeAgGridImportTrigger();
                  return { Component: CoverFlowPage };
                }}
              />
              <Route path={INBOX_PATH} element={<InboxPage />} />
              <Route element={<IsManager />}>
                <Route path={`${OPERATIONS_ROOT_PATH}/*`}>
                  <Route
                    element={
                      <ListingsContextProvider>
                        <Outlet />
                      </ListingsContextProvider>
                    }
                    path={OPERATIONS_LISTINGS_TERMINAL_PATH}
                  >
                    <Route
                      path={`${OPERATIONS_LISTINGS_CALENDAR_TERMINAL_PATH}/*`}
                      element={
                        <Navigate
                          to={OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH}
                          replace
                        />
                      }
                    />
                    <Route
                      element={
                        <Navigate
                          to={OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH}
                        />
                      }
                      index
                    />
                    <Route path={OPERATIONS_LISTINGS_FLOOR_PLAN_TERMINAL_PATH}>
                      <Route
                        element={
                          <Navigate
                            to={OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_PATH}
                          />
                        }
                        index
                      />
                      <Route
                        element={<ListingsFloorPlanPage />}
                        path={
                          OPERATIONS_LISTINGS_FLOOR_PLAN_PUBLISHED_TERMINAL_PATH
                        }
                      />
                      <Route
                        element={<ListingsFloorPlanPage />}
                        path={
                          OPERATIONS_LISTINGS_FLOOR_PLAN_INACTIVE_TERMINAL_PATH
                        }
                      />
                    </Route>
                    <Route
                      element={<CreateListingPage />}
                      path={OPERATIONS_LISTINGS_CREATE_TERMINAL_PATH}
                    />
                    <Route
                      element={<EditListingPage />}
                      path={`${OPERATIONS_LISTINGS_EDIT_TERMINAL_PATH}/:listingId`}
                    />
                  </Route>

                  <Route
                    path={OPERATIONS_RESERVATION_TIMELINE_TERMINAL_PATH}
                    element={<ReservationTimelinePage />}
                  />

                  <Route path={OPERATIONS_BULLETIN_BOARD_TERMINAL_PATH}>
                    <Route element={<RestaurantBulletinsPage />} index />
                    <Route
                      element={<RestaurantBulletinForm />}
                      path={OPERATIONS_BULLETIN_BOARD_CREATE_TERMINAL_PATH}
                    />
                    <Route
                      element={<RestaurantBulletinForm />}
                      path={OPERATIONS_BULLETIN_BOARD_UPDATE_TERMINAL_PATH}
                    />
                  </Route>

                  <Route path={OPERATIONS_EVENTS_TERMINAL_PATH}>
                    <Route index element={<RestaurantEventsPage />} />
                    <Route
                      path={OPERATIONS_EVENT_BOOK_TERMINAL_PATH}
                      lazy={async () => {
                        const { EventBookingPage } = await import(
                          './operations/restaurantEvents/booking/EventBookingPage'
                        );
                        return { Component: EventBookingPage };
                      }}
                    />
                    <Route
                      path={OPERATIONS_EVENT_DETAIL_NEW_TERMINAL_PATH}
                      element={<Navigate to={OPERATIONS_EVENTS_PATH} replace />}
                    />
                    <Route
                      path={OPERATIONS_EVENT_DETAIL_TERMINAL_PATH}
                      element={<Navigate to={OPERATIONS_EVENTS_PATH} replace />}
                    />
                  </Route>

                  <Route
                    path="events-v2"
                    element={<Navigate to={OPERATIONS_EVENTS_PATH} replace />}
                  />

                  <Route path={OPERATIONS_MESSAGING_TERMINAL_PATH}>
                    <Route index element={<MessagingPage />} />
                    <Route
                      path={OPERATIONS_MESSAGING_TEMPLATE_PATH}
                      element={<MessagingTemplatePage />}
                    />
                  </Route>

                  <Route
                    path={OPERATIONS_FLOOR_PLANS_TERMINAL_PATH}
                    index
                    element={<FloorPlansPage />}
                  />

                  <Route
                    path={OPERATIONS_SHIFTS_TERMINAL_PATH}
                    index
                    lazy={async () => {
                      const { ShiftsPage } = await import(
                        /* webpackPrefetch: true */ './operations/shifts/ShiftsPage'
                      );
                      return { Component: ShiftsPage };
                    }}
                  />

                  <Route
                    path={OPERATIONS_PRICING_GRID_TERMINAL_PATH}
                    index
                    lazy={async () => {
                      const { PricingGridPage } =
                        await pagesThatConsumeAgGridImportTrigger();
                      return { Component: PricingGridPage };
                    }}
                  />

                  <Route
                    path={OPERATIONS_FORECAST_TERMINAL_PATH}
                    element={<ForecastPage />}
                  />

                  <Route
                    path={OPERATIONS_BUSY_TIMES_TERMINAL_PATH}
                    element={<BusyTimesPage />}
                  />

                  <Route
                    path={OPERATIONS_TABLE_RATING_TERMINAL_PATH}
                    index
                    element={<TableRatingPage />}
                  />

                  {/* One child today, Hotel Sync. A second integration adds a sibling Route here
                      and an entry in PrimaryNavigationTabs' integrationsOperationsItems -- see
                      the README, "Known follow-ups". */}
                  <Route path={OPERATIONS_INTEGRATIONS_TERMINAL_PATH}>
                    <Route
                      index
                      element={
                        <Navigate
                          to={OPERATIONS_INTEGRATIONS_HOTEL_SYNC_PATH}
                          replace
                        />
                      }
                    />
                    <Route
                      path={OPERATIONS_INTEGRATIONS_HOTEL_SYNC_TERMINAL_PATH}
                      lazy={async () => {
                        const { HotelSyncPage } =
                          await pagesThatConsumeAgGridImportTrigger();
                        return { Component: HotelSyncPage };
                      }}
                    />
                  </Route>
                </Route>
                <Route
                  path="/restaurants/:restaurantId/floor-plans/:floorPlanId/edit"
                  index
                  lazy={async () => {
                    const { FloorPlanEditorPage } = await import(
                      /* webpackPrefetch: true */ './operations/floorPlans/FloorPlanEditorPage'
                    );
                    return { Component: FloorPlanEditorPage };
                  }}
                />
                <Route
                  path="/restaurants/:restaurantId/floor-plans/:floorPlanId/edit/table-combinations"
                  index
                  lazy={async () => {
                    const { FloorPlanTableCombinationsPage } = await import(
                      /* webpackPrefetch: true */ './operations/floorPlans/FloorPlanTableCombinationsPage'
                    );
                    return { Component: FloorPlanTableCombinationsPage };
                  }}
                />
                <Route element={<IsManager />}>
                  <Route
                    path={`${SETTINGS_ROOT_PATH}/*`}
                    element={<SettingsPage />}
                  >
                    <Route
                      index
                      element={<Navigate to={SETTINGS_GENERAL_PATH} replace />}
                    />
                    <Route
                      element={<GeneralPage />}
                      path={SETTINGS_GENERAL_TERMINAL_PATH}
                    />
                    <Route
                      element={<VipProgramPage />}
                      path={SETTINGS_VIP_PROGRAM_TERMINAL_PATH}
                    />
                    <Route
                      element={<TeamPage />}
                      path={SETTINGS_TEAM_TERMINAL_PATH}
                    />
                    <Route
                      element={<EditTeamMemberPage />}
                      path={`${SETTINGS_EDIT_TEAM_MEMBER_TERMINAL_PATH}/:adminId`}
                    />
                    <Route
                      element={<InviteTeamMemberPage />}
                      path={SETTINGS_INVITE_MEMBER_TERMINAL_PATH}
                    />
                    <Route
                      element={<FinancesPage />}
                      path={SETTINGS_FINANCES_TERMINAL_PATH}
                    />
                    <Route
                      element={<ReportsPage />}
                      path={SETTINGS_REPORTS_TERMINAL_PATH}
                    />
                    <Route
                      element={<LightspeedSettingsPage />}
                      path={SETTINGS_LIGHTSPEED_TERMINAL_PATH}
                    />
                    <Route
                      element={<LightspeedOAuthCallbackPage />}
                      path={SETTINGS_LIGHTSPEED_CALLBACK_TERMINAL_PATH}
                    />
                  </Route>
                </Route>
                <Route path={`${SUPPORT_ROOT_PATH}/*`}>
                  <Route
                    element={<GuestImport />}
                    path={SUPPORT_GUEST_IMPORT_TERMINAL_PATH}
                  />
                </Route>
              </Route>
            </Route>
          </Route>
          <Route element={<NotFoundHero />} path="*" />
        </Route>

        <Route
          element={<DailyReservationsReportPage />}
          path={RESERVATIONS_RESERVATIONS_REPORT_PATH}
        />
        <Route
          element={<DailyCoversReportPage />}
          path={RESERVATIONS_COVERS_REPORT_PATH}
        />
      </Route>
    </Route>,
  ),
);
